import React, { useCallback, useEffect, useState } from "react";
import { Select, Button, message } from "antd";
import magicIcon from "../../../assets/icons/magic.png";
import "./GenerateWithAI.scss";

const { Option, OptGroup } = Select;

type Provider = "openai" | "mistral";

interface ModelOption {
  label: string;
  value: string;
  provider: Provider;
}

const OPENAI_MODELS: ModelOption[] = [
  { label: "GPT-4o (Best Quality)", value: "gpt-4o", provider: "openai" },
  {
    label: "GPT-4o Mini (Fast & Efficient)",
    value: "gpt-4o-mini",
    provider: "openai",
  },
  {
    label: "GPT-4.1 (Advanced Reasoning)",
    value: "gpt-4.1",
    provider: "openai",
  },
  {
    label: "GPT-4.1 Mini (Balanced Speed)",
    value: "gpt-4.1-mini",
    provider: "openai",
  },
  {
    label: "GPT-4.1 Nano (Lightweight Tasks)",
    value: "gpt-4.1-nano",
    provider: "openai",
  },
  {
    label: "GPT-4 Turbo (High Performance)",
    value: "gpt-4-turbo",
    provider: "openai",
  },
  {
    label: "GPT-4 Turbo Preview (Latest Preview)",
    value: "gpt-4-turbo-preview",
    provider: "openai",
  },
  {
    label: "GPT-3.5 Turbo (Reliable Generation)",
    value: "gpt-3.5-turbo",
    provider: "openai",
  },
  {
    label: "GPT-3.5 Turbo 16K (Long Context)",
    value: "gpt-3.5-turbo-16k",
    provider: "openai",
  },
];

const MISTRAL_MODELS: ModelOption[] = [
  // Frontier Models (Best Performance)
  {
    label: "Magistral Medium Latest (Reasoning Focused)",
    value: "magistral-medium-latest",
    provider: "mistral",
  },
  {
    label: "Mistral Large Latest (High Quality)",
    value: "mistral-large-latest",
    provider: "mistral",
  },
  {
    label: "Mistral Medium Latest (Balanced Output)",
    value: "mistral-medium-latest",
    provider: "mistral",
  },
  {
    label: "Pixtral Large Latest (Vision + Text)",
    value: "pixtral-large-latest",
    provider: "mistral",
  },

  // Small/Balanced Models
  {
    label: "Mistral Small Latest (Fast & Light)",
    value: "mistral-small-latest",
    provider: "mistral",
  },
  {
    label: "Mistral Tiny Latest (Ultra Lightweight)",
    value: "mistral-tiny-latest",
    provider: "mistral",
  },

  // Specialized Models
  {
    label: "Codestral Latest (Code Generation)",
    value: "codestral-latest",
    provider: "mistral",
  },

  // Compact/Edge Models
  {
    label: "Ministral 8B (Compact Power)",
    value: "ministral-8b-latest",
    provider: "mistral",
  },
  {
    label: "Ministral 3B (Fastest Option)",
    value: "ministral-3b-latest",
    provider: "mistral",
  },

  // Open Source
  {
    label: "Mistral 7B (Open Source)",
    value: "open-mistral-7b",
    provider: "mistral",
  },
];

const DEFAULT_MODEL = "gpt-4o-mini";

export interface GenerateWithAIProps {
  className?: string;
  model?: string;
  defaultModel?: string;
  onModelChange?: (model: string) => void;
  onGenerate: (model: string) => Promise<void> | void;
  loading?: boolean;
  disabled?: boolean;
  buttonText?: string;
  selectLabel?: string;
  selectPlaceholder?: string;
  showIcon?: boolean;
}

const GenerateWithAI: React.FC<GenerateWithAIProps> = ({
  className,
  model,
  defaultModel = DEFAULT_MODEL,
  onModelChange,
  onGenerate,
  loading = false,
  disabled = false,
  buttonText = "Generate using AI",
  selectLabel = "Select AI model",
  selectPlaceholder = "Choose a model",
  showIcon = true,
}) => {
  const [internalModel, setInternalModel] = useState<string>(defaultModel);

  useEffect(() => {
    setInternalModel(defaultModel);
  }, [defaultModel]);

  const activeModel = model ?? internalModel;

  const handleModelChange = useCallback(
    (value: string) => {
      if (onModelChange) {
        onModelChange(value);
      } else {
        setInternalModel(value);
      }
    },
    [onModelChange]
  );

  const handleGenerate = useCallback(async () => {
    if (!activeModel) {
      message.warning("Please select an AI model to continue.");
      return;
    }

    await onGenerate(activeModel);
  }, [activeModel, onGenerate]);

  const containerClass = ["generate-with-ai", className]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    <div className={containerClass}>
      <div className="generate-with-ai__card">
        <div className="generate-with-ai__header">
          {showIcon && <img src={magicIcon} alt="AI wand" />}
          <div className="generate-with-ai__title-group">
            <span className="generate-with-ai__title">Generate with AI</span>
            <span className="generate-with-ai__subtitle">
              Pick a model and let AI draft the content for you.
            </span>
          </div>
        </div>
        <div className="generate-with-ai__controls">
          <div className="generate-with-ai__select">
            <label>{selectLabel}</label>
            <Select
              value={activeModel}
              onChange={handleModelChange}
              placeholder={selectPlaceholder}
              size="large"
              showSearch
              optionFilterProp="children"
              dropdownClassName="generate-with-ai__dropdown"
              disabled={loading || disabled}
            >
              <OptGroup label="OpenAI">
                {OPENAI_MODELS.map((item) => (
                  <Option key={item.value} value={item.value}>
                    {item.label}
                  </Option>
                ))}
              </OptGroup>
              <OptGroup label="Mistral">
                {MISTRAL_MODELS.map((item) => (
                  <Option key={item.value} value={item.value}>
                    {item.label}
                  </Option>
                ))}
              </OptGroup>
            </Select>
          </div>
          <Button
            size="large"
            className="generate-with-ai__button"
            onClick={handleGenerate}
            loading={loading}
            disabled={disabled}
            style={{
              background: "rgba(188, 150, 35, 0.15)",
              color: "rgba(188, 150, 35, 1)",
              border: "none",
              borderRadius: 9999,
              height: 36,
              paddingInline: 10,
              fontSize: 14,
            }}
          >
            {!loading && showIcon && (
              <img
                style={{ marginRight: 5 }}
                src={magicIcon}
                alt="Generate"
                className="generate-with-ai__button-icon"
              />
            )}
            {loading ? "Generating..." : buttonText}
          </Button>
        </div>
      </div>
      <div className="generate-with-ai__separator">
        <span>OR</span>
      </div>
    </div>
  );
};

export default GenerateWithAI;
